import { useState, useRef, useCallback, useEffect } from "react";
import {
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCircle2,
  Loader2,
  Upload,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

type UploadStatus = "idle" | "dragging" | "processing" | "success" | "error";

type ExtractedField = {
  key: string;
  label: string;
  value: string;
};

type FileUploadSectionProps = {
  onUpload: (files: File[]) => Promise<void>;
  isExtracting: boolean;
  autoFocus?: boolean;
  accept?: string;
};

function FileUploadSection({
  onUpload,
  isExtracting,
  autoFocus,
  accept = ".pdf,.xlsx,.xls",
}: FileUploadSectionProps) {
  const [status, setStatus] = useState<UploadStatus>("idle");
  const [fileName, setFileName] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [extractedFields, setExtractedFields] = useState<ExtractedField[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoFocus && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [autoFocus]);

  useEffect(() => {
    if (isExtracting) {
      setStatus("processing");
    }
  }, [isExtracting]);

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const file = files[0];
      const ext = file.name.toLowerCase();
      if (
        !ext.endsWith(".pdf") &&
        !ext.endsWith(".xlsx") &&
        !ext.endsWith(".xls")
      ) {
        setStatus("error");
        setErrorMessage("Doar fișiere PDF și Excel (.xlsx) sunt acceptate.");
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setStatus("error");
        setErrorMessage("Fișierul depășește limita de 10MB.");
        return;
      }

      setFileName(file.name);
      setStatus("processing");
      setErrorMessage(null);

      try {
        await onUpload([file]);
        setStatus("success");
        setExtractedFields([]);
      } catch {
        setStatus("error");
        setErrorMessage("Eroare la procesarea documentului.");
      }
    },
    [onUpload]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setStatus("dragging");
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setStatus("idle");
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
    },
    [handleFiles]
  );

  const handleRemoveFile = useCallback(() => {
    setStatus("idle");
    setFileName(null);
    setErrorMessage(null);
    setExtractedFields([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleRetry = useCallback(() => {
    handleRemoveFile();
    fileInputRef.current?.click();
  }, [handleRemoveFile]);

  return (
    <div ref={sectionRef}>
      {status === "idle" || status === "dragging" ? (
        <IdleUploadZone
          status={status}
          fileInputRef={fileInputRef}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onFileChange={handleFileChange}
          accept={accept}
        />
      ) : null}

      {status === "processing" ? <ProcessingState fileName={fileName} /> : null}

      {status === "success" ? (
        <SuccessState
          extractedFields={extractedFields}
          onRemove={handleRemoveFile}
        />
      ) : null}

      {status === "error" ? (
        <ErrorState errorMessage={errorMessage} onRetry={handleRetry} />
      ) : null}
    </div>
  );
}

function getAcceptHint(accept: string): string {
  const hasPdf = accept.includes(".pdf");
  const hasExcel = accept.includes(".xlsx") || accept.includes(".xls");
  if (hasPdf && hasExcel) return "PDF sau Excel (max. 10MB)";
  if (hasPdf) return "PDF (max. 10MB)";
  if (hasExcel) return "Excel (max. 10MB)";
  return "Max. 10MB";
}

function IdleUploadZone({
  status,
  fileInputRef,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileChange,
  accept,
}: {
  status: "idle" | "dragging";
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  accept: string;
}) {
  const isDragging = status === "dragging";

  return (
    <label
      htmlFor="pdf-upload"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={cn(
        "flex flex-col items-center justify-center w-full h-28 md:h-56 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-300",
        isDragging
          ? "border-ds-blue-500 bg-ds-blue-50 scale-[1.01]"
          : "border-ds-border-300 hover:border-ds-border-400 hover:bg-ds-surface-50"
      )}
    >
      <div className="flex flex-row md:flex-col items-center justify-center gap-3">
        <div
          className={cn(
            "w-9 h-9 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-colors shrink-0",
            isDragging ? "bg-ds-blue-100" : "bg-ds-surface-100"
          )}
        >
          <Upload
            className={cn(
              "w-4 h-4 md:w-6 md:h-6 transition-colors",
              isDragging ? "text-ds-blue-600" : "text-ds-text-400"
            )}
          />
        </div>
        <div className="text-left md:text-center">
          <p className="text-xs md:text-sm text-ds-text-600">
            <span className="font-semibold text-ds-blue-600">
              Click pentru a încărca
            </span>{" "}
            <span className="hidden md:inline">sau trage fișierul aici</span>
          </p>
          <p className="text-[11px] md:text-xs text-ds-text-400 mt-0.5 md:mt-1">
            {getAcceptHint(accept)}
          </p>
        </div>
      </div>
      <input
        ref={fileInputRef}
        id="pdf-upload"
        type="file"
        className="hidden"
        accept={accept}
        onChange={onFileChange}
      />
    </label>
  );
}

function ProcessingState({ fileName }: { fileName: string | null }) {
  return (
    <div className="w-full border-2 border-ds-blue-200 rounded-xl p-6 bg-ds-blue-50/50">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-ds-blue-100 flex items-center justify-center shrink-0">
          <Loader2 className="w-5 h-5 text-ds-blue-600 animate-spin" />
        </div>
        <div className="flex-1 min-w-0">
          {fileName && (
            <p className="font-medium text-navy-900 text-sm truncate">
              {fileName}
            </p>
          )}
          <p className="text-xs text-ds-text-500">
            Se extrag datele din document...
          </p>
          <Progress
            value={60}
            className="h-1.5 mt-2 bg-ds-blue-100"
            indicatorClassName="bg-ds-blue-500 transition-all"
          />
        </div>
      </div>
    </div>
  );
}

function SuccessState({
  extractedFields,
  onRemove,
}: {
  extractedFields: ExtractedField[];
  onRemove: () => void;
}) {
  return (
    <div className="w-full border-2 border-ds-emerald-200 rounded-xl overflow-hidden">
      <div className="bg-ds-emerald-50 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-ds-emerald-600" />
          <span className="text-sm font-semibold text-ds-emerald-800">
            Datele au fost extrase cu succes
          </span>
        </div>
        <Button variant="ghost" size="sm" onClick={onRemove}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      {extractedFields.length > 0 && (
        <div className="p-4 space-y-2">
          <div className="grid grid-cols-2 gap-2 text-xs">
            {extractedFields.map((field) => (
              <div
                key={field.key}
                className="flex items-center gap-2 p-2 bg-ds-emerald-50/50 rounded-lg"
              >
                <Check className="w-3.5 h-3.5 text-ds-emerald-500 shrink-0" />
                <div>
                  <span className="text-ds-text-500">{field.label}:</span>{" "}
                  <span className="font-medium text-navy-900">
                    {field.value}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="px-4 pb-3">
        <p className="text-xs text-ds-amber-600 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          Unele câmpuri necesită completare manuală (date financiare, contacte)
        </p>
      </div>
    </div>
  );
}

function ErrorState({
  errorMessage,
  onRetry,
}: {
  errorMessage: string | null;
  onRetry: () => void;
}) {
  return (
    <div className="w-full border-2 border-ds-red-200 rounded-xl p-4 bg-ds-red-50/50">
      <div className="flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-ds-red-500 shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-ds-red-800">
            {errorMessage || "Eroare la procesarea documentului"}
          </p>
          <p className="text-xs text-ds-red-600 mt-0.5">
            Verifică formatul fișierului și încearcă din nou.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRetry}
          className="shrink-0"
        >
          Reîncearcă
        </Button>
      </div>
    </div>
  );
}

export default FileUploadSection;
